<template>
  <div class="reservas-owner">
    <h2 class="subheading grey--text">Reservas de Estacionamiento</h2>

    <v-container class="my-5">

      <!-- Card principal con toolbar -->
      <v-layout align-center>
        <v-flex>
          <v-card class="elevation-5">
            <v-toolbar dark color="primary" class="mb-2">
              <v-icon class="mr-2">event_note</v-icon>
              <v-toolbar-title>Mis Reservas</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn dark flat @click="openCreateDialog">
                <v-icon left>add</v-icon>
                Nueva Reserva
              </v-btn>
            </v-toolbar>

            <v-card-text>
              <!-- Filtros -->
              <v-layout row wrap align-center class="mb-3">
                <v-flex xs12 sm6 md3 class="pr-2">
                  <v-select
                    v-model="filterStatus"
                    :items="statusOptions"
                    label="Estado"
                    prepend-icon="filter_list"
                    clearable
                    hide-details
                  ></v-select>
                </v-flex>
                <v-flex xs12 sm6 md3 class="pr-2">
                  <v-menu ref="startMenu" v-model="startMenu" :close-on-content-click="false" offset-y lazy>
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="formatDate(dateRange.start)"
                        label="Desde"
                        prepend-icon="calendar_today"
                        readonly
                        clearable
                        hide-details
                        v-on="on"
                        @click:clear="dateRange.start = null"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="dateRange.start" @input="startMenu = false"></v-date-picker>
                  </v-menu>
                </v-flex>
                <v-flex xs12 sm6 md3>
                  <v-menu ref="endMenu" v-model="endMenu" :close-on-content-click="false" offset-y lazy>
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="formatDate(dateRange.end)"
                        label="Hasta"
                        prepend-icon="calendar_today"
                        readonly
                        clearable
                        hide-details
                        v-on="on"
                        @click:clear="dateRange.end = null"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="dateRange.end" @input="endMenu = false"></v-date-picker>
                  </v-menu>
                </v-flex>
              </v-layout>

              <!-- Alerta error -->
              <v-alert :value="!!error" type="warning" outline transition="scale-transition" class="mb-3">
                {{ error }}
              </v-alert>
            </v-card-text>

            <!-- Tabla -->
            <v-data-table
              :headers="headers"
              :items="filteredReservations"
              :loading="loading"
              :pagination.sync="pagination"
              :rows-per-page-text="'Filas por página'"
              :rows-per-page-items="[10, 25, 50, { text: 'Todos', value: -1 }]"
              :no-data-text="'No hay reservas registradas'"
              class="elevation-1"
            >
              <template v-slot:items="props">
                <!-- Empresa -->
                <td>
                  <span class="font-weight-medium">{{ props.item.beneficiary_company_name }}</span>
                </td>
                <!-- Período -->
                <td>
                  <span>{{ formatDate(props.item.start_date) }}</span>
                  <span class="grey--text"> — {{ formatDate(props.item.end_date) }}</span>
                </td>
                <!-- Cajones -->
                <td>
                  <v-chip v-if="props.item.reserved_spaces && props.item.reserved_spaces.length > 0" small color="primary" text-color="white" class="mr-2">
                    {{ props.item.reserved_spaces.length }}
                  </v-chip>
                  <span v-if="props.item.reserved_spaces && props.item.reserved_spaces.length > 0" class="grey--text caption">({{ props.item.reserved_spaces.map(s => s.space_number).join(', ') }})</span>
                  <span v-else class="grey--text caption">Sin cajones</span>
                </td>
                <!-- Tipo -->
                <td>
                  <v-chip
                    :color="props.item.is_private_vehicles ? 'info' : 'warning'"
                    text-color="white"
                    small
                  >
                    <v-icon left x-small>{{ props.item.is_private_vehicles ? 'directions_car' : 'local_shipping' }}</v-icon>
                    {{ props.item.is_private_vehicles ? 'Privados' : 'Transportistas' }}
                  </v-chip>
                </td>
                <!-- Estado -->
                <td>
                  <v-chip
                    :color="getStatusColor(props.item.status)"
                    text-color="white"
                    small
                  >
                    {{ formatStatus(props.item.status) }}
                  </v-chip>
                </td>
                <!-- Acciones -->
                <td class="justify-center layout px-0">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon medium class="mr-2" v-on="on" @click="viewReservation(props.item)">visibility</v-icon>
                    </template>
                    <span>Ver detalles</span>
                  </v-tooltip>
                  <v-tooltip bottom v-if="props.item.status === 'active'">
                    <template v-slot:activator="{ on }">
                      <v-icon medium class="mr-2" color="blue" v-on="on" @click="openExtendDialog(props.item)">date_range</v-icon>
                    </template>
                    <span>Modificar reserva</span>
                  </v-tooltip>
                  <v-tooltip bottom v-if="props.item.status === 'active'">
                    <template v-slot:activator="{ on }">
                      <v-icon medium color="red" v-on="on" @click="confirmCancel(props.item)">cancel</v-icon>
                    </template>
                    <span>Cancelar reserva</span>
                  </v-tooltip>
                </td>
              </template>
            </v-data-table>

          </v-card>
        </v-flex>
      </v-layout>

    </v-container>

    <!-- Dialog: Crear/Editar Reserva -->
    <ReservationDialog
      :dialog="dialogReservation"
      :mode="dialogMode"
      :companies="companies"
      :parking-spaces="parkingSpaces"
      :initial-data="selectedReservation"
      @created="handleReservationCreated"
      @close="dialogReservation = false"
    />

    <!-- Dialog: Detalles Reserva -->
    <v-dialog v-model="dialogDetails" max-width="600px">
      <v-card v-if="selectedReservation">
        <v-toolbar dark color="primary">
          <v-icon class="mr-2">info</v-icon>
          <v-toolbar-title>Detalles de Reserva</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon color="red" @click="dialogDetails = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4">
          <v-layout row wrap>
            <v-flex xs12 sm6 class="pr-3">
              <v-text-field
                :value="selectedReservation.beneficiary_company_name"
                label="Empresa Beneficiaria"
                prepend-icon="business"
                readonly
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="pl-3">
              <v-text-field
                :value="formatStatus(selectedReservation.status)"
                label="Estado"
                prepend-icon="info"
                readonly
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="pr-3">
              <v-text-field
                :value="formatDate(selectedReservation.start_date)"
                label="Fecha Inicio"
                prepend-icon="event"
                readonly
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="pl-3">
              <v-text-field
                :value="formatDate(selectedReservation.end_date)"
                label="Fecha Fin"
                prepend-icon="event"
                readonly
              ></v-text-field>
            </v-flex>

            <!-- Horarios (si existen) -->
            <v-flex xs12 sm6 class="pr-3" v-if="selectedReservation.start_time || selectedReservation.end_time">
              <v-text-field
                :value="selectedReservation.start_time ? formatTime(selectedReservation.start_time) : '-'"
                label="Hora Inicio"
                prepend-icon="access_time"
                readonly
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="pl-3" v-if="selectedReservation.start_time || selectedReservation.end_time">
              <v-text-field
                :value="selectedReservation.end_time ? formatTime(selectedReservation.end_time) : '-'"
                label="Hora Fin"
                prepend-icon="access_time"
                readonly
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6 class="pr-3">
              <v-text-field
                :value="selectedReservation.is_private_vehicles ? 'Vehículos privados/particulares' : 'Transportistas/Proveedores'"
                label="Tipo de Reserva"
                prepend-icon="local_parking"
                readonly
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="pl-3">
              <v-text-field
                :value="selectedReservation.notes || 'Sin notas'"
                label="Notas"
                prepend-icon="note"
                readonly
              ></v-text-field>
            </v-flex>
          </v-layout>

          <p class="subheading grey--text mt-4 mb-2">Cajones reservados ({{ selectedReservation.reserved_spaces && selectedReservation.reserved_spaces.length || 0 }})</p>
          <v-list v-if="selectedReservation.reserved_spaces && selectedReservation.reserved_spaces.length > 0" two-line>
            <v-list-tile v-for="(space, i) in selectedReservation.reserved_spaces" :key="'space-' + space.id">
              <v-list-tile-action>
                <v-icon color="primary">local_parking</v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title><strong>Cajón {{ space.space_number }}</strong></v-list-tile-title>
                <v-list-tile-sub-title v-if="space.section_name">{{ space.section_name }}</v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-divider v-for="i in selectedReservation.reserved_spaces.length - 1" :key="'divider-' + i"></v-divider>
          </v-list>
          <v-alert v-else :value="true" type="info" outline class="mt-2">
            No hay cajones registrados para esta reserva
          </v-alert>

          <!-- Transportistas -->
          <p class="subheading grey--text mt-4 mb-2">Transportistas ({{ selectedReservation.providers && selectedReservation.providers.length || 0 }})</p>
          <v-data-table
            v-if="selectedReservation.providers && selectedReservation.providers.length > 0"
            :headers="providerHeaders"
            :items="selectedReservation.providers"
            :rows-per-page-items="[5, 10, 25]"
            class="elevation-1"
            hide-actions
          >
            <template v-slot:items="props">
              <td>
                <span class="font-weight-medium">{{ props.item.first_name }} {{ props.item.last_name }}</span>
              </td>
              <td>
                <a :href="'tel:' + props.item.telephone" class="text-decoration-none">{{ props.item.telephone }}</a>
              </td>
              <td>
                <a :href="'mailto:' + props.item.email" class="text-decoration-none">{{ props.item.email }}</a>
              </td>
              <td>
                <v-chip small color="info" text-color="white">{{ props.item.plates }}</v-chip>
              </td>
              <td>
                <span class="caption">{{ props.item.unit_specs || '-' }}</span>
              </td>
              <td>
                <v-chip
                  :color="getQRStatusColor(props.item.qr_status)"
                  text-color="white"
                  small
                >
                  {{ getQRStatusLabel(props.item.qr_status) }}
                </v-chip>
              </td>
              <td class="text-center">
                <v-tooltip bottom v-if="props.item.ine">
                  <template v-slot:activator="{ on }">
                    <v-icon
                      small
                      color="primary"
                      class="cursor-pointer"
                      v-on="on"
                      @click="openIneDialog(props.item)"
                    >
                      image
                    </v-icon>
                  </template>
                  <span>Ver documento INE</span>
                </v-tooltip>
                <span v-else class="grey--text caption">-</span>
              </td>
            </template>
          </v-data-table>
          <v-alert v-else :value="true" type="info" outline class="mt-2">
            No hay proveedores registrados para esta reserva
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="dialogDetails = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Ver Documento INE -->
    <v-dialog v-model="dialogIne" max-width="700px">
      <v-card v-if="selectedProvider">
        <v-toolbar dark color="primary">
          <v-icon class="mr-2">image</v-icon>
          <v-toolbar-title>Documento INE - {{ selectedProvider.first_name }} {{ selectedProvider.last_name }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon color="red" @click="dialogIne = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4 text-center">
          <img
            v-if="selectedProvider.ine"
            :src="selectedProvider.ine"
            style="max-width: 100%; max-height: 600px; object-fit: contain;"
            :alt="'INE de ' + selectedProvider.first_name"
          />
          <v-alert v-else :value="true" type="warning" outline>
            No hay documento INE disponible
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="dialogIne = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Confirmar Cancelación -->
    <v-dialog v-model="dialogConfirmCancel" max-width="400px">
      <v-card>
        <v-toolbar dark color="primary">
          <v-icon class="mr-2">warning</v-icon>
          <v-toolbar-title>Confirmar Cancelación</v-toolbar-title>
        </v-toolbar>

        <v-card-text class="pa-4">
          <p class="body-1">
            ¿Está seguro de que desea cancelar la reserva para
            <strong>{{ selectedReservation && selectedReservation.beneficiary_company_name }}</strong>?
          </p>
          <p class="grey--text caption">Esta acción no se puede deshacer.</p>
        </v-card-text>

        <v-alert :value="!!errorCancel" type="error" outline class="mx-4 mb-2">
          {{ errorCancel }}
        </v-alert>

        <v-card-actions>
          <v-btn color="primary" @click="dialogConfirmCancel = false">No</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="red" dark :loading="loadingCancel" @click="handleCancelReservation">Sí, cancelar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Extender Reserva -->
    <v-dialog v-model="dialogExtend" max-width="600px">
      <v-card>
        <v-toolbar dark color="primary">
          <v-icon class="mr-2">date_range</v-icon>
          <v-toolbar-title>Modificar Reserva</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon color="red" @click="dialogExtend = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4">
          <v-alert :value="!!errorExtend" type="error" class="mb-3">
            {{ errorExtend }}
          </v-alert>

          <p class="subheading mb-3">
            Modificar reserva de <strong>{{ selectedReservation && selectedReservation.beneficiary_company_name }}</strong>
          </p>

          <v-form ref="formExtend" v-model="formExtendValid">
            <v-layout row wrap>
              <!-- Fechas -->
              <v-flex xs12 sm6 class="pr-2">
                <v-menu ref="startDateMenu" v-model="startDateMenu" :close-on-content-click="false" offset-y lazy>
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="extendData.start_date"
                      label="Fecha inicio"
                      prepend-icon="event"
                      readonly
                      hide-details
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="extendData.start_date" @input="startDateMenu = false"></v-date-picker>
                </v-menu>
              </v-flex>

              <v-flex xs12 sm6 class="pl-2">
                <v-menu ref="endDateMenu" v-model="endDateMenu" :close-on-content-click="false" offset-y lazy>
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="extendData.end_date"
                      label="Fecha fin"
                      prepend-icon="event"
                      readonly
                      :rules="[rules.required]"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="extendData.end_date" @input="endDateMenu = false"></v-date-picker>
                </v-menu>
              </v-flex>

              <!-- Horas -->
              <v-flex xs12 sm6 class="pr-2 mt-2">
                <v-text-field
                  v-model="extendData.start_time"
                  label="Hora inicio"
                  type="time"
                  prepend-icon="access_time"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 sm6 class="pl-2 mt-2">
                <v-text-field
                  v-model="extendData.end_time"
                  label="Hora fin"
                  type="time"
                  prepend-icon="access_time"
                ></v-text-field>
              </v-flex>

              <!-- Espacios de Estacionamiento -->
              <v-flex xs12 class="mt-3">
                <v-select
                  v-model="extendData.parking_space_ids"
                  :items="parkingSpaceOptions"
                  item-text="text"
                  item-value="id"
                  label="Espacios de estacionamiento"
                  prepend-icon="local_parking"
                  multiple
                  chips
                  hide-details
                ></v-select>
              </v-flex>

              <!-- Tipo de Reserva -->
              <v-flex xs12 class="mt-3">
                <v-checkbox
                  v-model="extendData.is_private_vehicles"
                  label="Vehículos privados/particulares"
                  color="primary"
                  hide-details
                  class="my-0"
                ></v-checkbox>
                <p class="caption grey--text mt-1 mb-0">
                  {{ extendData.is_private_vehicles ? 'Para automóviles privados' : 'Para transportistas/proveedores' }}
                </p>
              </v-flex>

              <!-- Notas -->
              <v-flex xs12 class="mt-3">
                <v-textarea
                  v-model="extendData.notes"
                  label="Notas (opcional)"
                  prepend-icon="note"
                  rows="2"
                  counter="256"
                ></v-textarea>
              </v-flex>
            </v-layout>
          </v-form>

        </v-card-text>

        <v-card-actions>
          <v-btn color="primary" @click="dialogExtend = false">Cancelar</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" :loading="loadingExtend" @click="handleExtendReservation">Modificar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import ReservationDialog from '@/components/parking/ReservationDialog.vue'
import moment from 'moment'
import axios from 'axios'
import store from '@/store'

export default {
  name: 'ReservationsOwner',
  components: {
    ReservationDialog
  },
  data() {
    return {
      loading: false,
      loadingCancel: false,
      loadingExtend: false,
      error: null,
      errorExtend: null,
      
      dialogReservation: false,
      dialogMode: 'create',
      dialogDetails: false,
      dialogIne: false,
      dialogConfirmCancel: false,
      errorCancel: null,
      dialogExtend: false,
      
      selectedReservation: null,
      selectedProvider: null,
      
      filterStatus: null,
      searchText: '',
      dateRange: {
        start: null,
        end: null
      },
      startMenu: false,
      endMenu: false,
      extendMenu: false,
      startDateMenu: false,
      endDateMenu: false,
      startTimeMenu: false,
      endTimeMenu: false,
      
      pagination: {
        sortBy: 'start_date',
        descending: true,
        page: 1,
        rowsPerPage: 10
      },

      headers: [
        { text: 'Empresa Beneficiaria', value: 'beneficiary_company_name', width: '22%' },
        { text: 'Período', value: 'period', width: '18%' },
        { text: 'Cajones', value: 'reserved_spaces', width: '18%' },
        { text: 'Tipo', value: 'is_private_vehicles', width: '12%' },
        { text: 'Estado', value: 'status', width: '12%' },
        { text: 'Acciones', value: 'actions', width: '12%', sortable: false }
      ],

      providerHeaders: [
        { text: 'Nombre', value: 'first_name', width: '18%' },
        { text: 'Teléfono', value: 'telephone', width: '15%' },
        { text: 'Email', value: 'email', width: '20%' },
        { text: 'Placas', value: 'plates', width: '12%' },
        { text: 'Especificaciones', value: 'unit_specs', width: '20%' },
        { text: 'Estado QR', value: 'qr_status', width: '10%' },
        { text: 'INE', value: 'ine', width: '5%', sortable: false }
      ],

      statusOptions: [
        { text: 'Activa', value: 'active' },
        { text: 'Expirada', value: 'expired' },
        { text: 'Cancelada', value: 'canceled' }
      ],

      companies: [],
      parkingSpaces: [],
      
      formExtendValid: false,
      extendData: {
        start_date: '',
        end_date: '',
        start_time: '',
        end_time: '',
        parking_space_ids: [],
        notes: '',
        is_private_vehicles: false
      },

      rules: {
        required: (v) => !!v || 'Campo requerido'
      }
    }
  },
  computed: {
    ...mapState('parking_reservations', ['reservations']),
    ...mapGetters(['getProfile']),

    filteredReservations() {
      let filtered = this.reservations
        .filter(r => r.role === 'owner') // Solo reservas donde soy dueño

      if (this.filterStatus) {
        filtered = filtered.filter(r => r.status === this.filterStatus)
      }

      if (this.searchText) {
        const search = this.searchText.toLowerCase()
        filtered = filtered.filter(r =>
          r.beneficiary_company_name.toLowerCase().includes(search)
        )
      }

      if (this.dateRange.start) {
        filtered = filtered.filter(r =>
          new Date(r.start_date) >= new Date(this.dateRange.start)
        )
      }

      if (this.dateRange.end) {
        filtered = filtered.filter(r =>
          new Date(r.end_date) <= new Date(this.dateRange.end)
        )
      }

      return filtered
    },

    parkingSpaceOptions() {
      return this.parkingSpaces.map(space => ({
        id: space.id,
        text: `Espacio ${space.number}`,
        section: space.section_name || ''
      }))
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    ...mapActions('parking_reservations', [
      'FETCH_RESERVATIONS',
      'CREATE_RESERVATION',
      'CANCEL_RESERVATION'
    ]),

    async fetchData() {
      this.loading = true
      this.error = null

      try {
        // Obtener reservas en paralelo con empresas y espacios
        await Promise.all([
          this.FETCH_RESERVATIONS({ role: 'owner' }),
          this.getLinkedCompanies(),
          this.getParkingSpaces()
        ])
      } catch (err) {
        this.error = 'Error al cargar datos'
        console.error(err)
      } finally {
        this.loading = false
      }
    },

    getLinkedCompanies() {
      return new Promise((resolve) => {
        const config = {
          method: 'get',
          url: 'https://empresa-service.intrare.app/getProvidersByAdmin',
          headers: {
            'Content-Type': 'application/json',
            'authorization': 'Token ' + store.getters.getToken
          }
        }
        
        axios(config)
          .then(response => {
            // Mapear la respuesta para extraer companyProvider
            const mappedCompanies = Array.isArray(response.data) ? response.data.map(item => ({
              id: item.companyProvider.id,
              name: item.companyProvider.name,
              address: item.companyProvider.address,
              email: item.companyProvider.email
            })) : []
            this.companies = mappedCompanies
            resolve(mappedCompanies)
          })
          .catch(error => {
            console.error('Error obtener empresas:', error)
            this.companies = []
            resolve([])
          })
      })
    },

    getParkingSpaces() {
      return new Promise((resolve) => {
        const config = {
          method: 'get',
          url: 'https://empresa-service.intrare.app/Parking/spaces/',
          headers: {
            'Content-Type': 'application/json',
            'authorization': 'Token ' + store.getters.getToken
          },
          params: {
            id_section__idEmpresa: this.getProfile.id_company,
            is_active: true
          }
        }
        
        axios(config)
          .then(response => {
            this.parkingSpaces = response.data
            resolve(response.data)
          })
          .catch(error => {
            console.error('Error obtener espacios:', error)
            this.parkingSpaces = []
            resolve([])
          })
      })
    },

    openCreateDialog() {
      this.dialogMode = 'create'
      this.selectedReservation = null
      this.dialogReservation = true
    },

    viewReservation(reservation) {
      this.selectedReservation = reservation
      this.dialogDetails = true
    },

    openIneDialog(provider) {
      this.selectedProvider = provider
      this.dialogIne = true
    },

    async handleReservationCreated() {
      this.dialogReservation = false
      await this.FETCH_RESERVATIONS({ role: 'owner' })
      this.$store.commit('snackbar_activate_true')
      this.$store.commit('snackbar_change_message', 'Reserva creada exitosamente')
    },

    confirmCancel(reservation) {
      this.selectedReservation = reservation
      this.errorCancel = null
      this.dialogConfirmCancel = true
    },

    async handleCancelReservation() {
      this.loadingCancel = true
      this.errorCancel = null

      try {
        console.log('Cancelando reserva ID:', this.selectedReservation.id)
        await this.CANCEL_RESERVATION(this.selectedReservation.id)
        this.dialogConfirmCancel = false
        await this.FETCH_RESERVATIONS({ role: 'owner' })
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Reserva cancelada exitosamente')
      } catch (err) {
        console.error('Error cancelando reserva:', err)
        if (err.response && err.response.status === 403) {
          this.errorCancel = 'No tienes permisos para cancelar esta reserva'
        } else if (err.response && err.response.status === 400) {
          this.errorCancel = (err.response.data && err.response.data.detail) || 'No se puede cancelar esta reserva'
        } else {
          this.errorCancel = err.message || 'Error al cancelar reserva'
        }
      } finally {
        this.loadingCancel = false
      }
    },

    openExtendDialog(reservation) {
      this.selectedReservation = reservation
      this.extendData = {
        start_date: reservation.start_date,
        end_date: reservation.end_date,
        start_time: reservation.start_time || '',
        end_time: reservation.end_time || '',
        parking_space_ids: reservation.reserved_spaces ? reservation.reserved_spaces.map(s => s.parking_space) : [],
        notes: reservation.notes || '',
        is_private_vehicles: reservation.is_private_vehicles || false
      }
      this.errorExtend = null
      this.dialogExtend = true
    },

    async handleExtendReservation() {
      // Validar que hay una reserva seleccionada
      if (!this.selectedReservation) {
        this.errorExtend = 'Error: no hay reserva seleccionada'
        return
      }

      // Validar fecha fin seleccionada
      if (!this.extendData.end_date) {
        this.errorExtend = 'Debes seleccionar una fecha de fin'
        return
      }

      // Validar que start_date existe
      const startDateStr = this.extendData.start_date || this.selectedReservation.start_date
      if (!startDateStr) {
        this.errorExtend = 'Error: no hay fecha de inicio'
        return
      }

      // Extraer solo la fecha (YYYY-MM-DD) para comparación
      const endDateOnly = this.extendData.end_date.substring(0, 10)
      const startDateOnly = startDateStr.substring(0, 10)

      var endDate = new Date(endDateOnly)
      var startDate = new Date(startDateOnly)

      // Validar: end_date >= start_date
      if (endDate < startDate) {
        this.errorExtend = 'La fecha de fin debe ser igual o posterior a la fecha de inicio'
        return
      }

      this.loadingExtend = true
      this.errorExtend = null

      try {
        // Construir objeto de datos con solo los campos que se van a actualizar
        const data = {
          end_date: this.extendData.end_date,
          is_private_vehicles: this.extendData.is_private_vehicles
        }

        // Agregar campos opcionales si tienen valor
        if (this.extendData.start_date && this.extendData.start_date !== this.selectedReservation.start_date) {
          data.start_date = this.extendData.start_date
        }
        if (this.extendData.start_time) {
          // Convertir formato HH:MM a HH:MM:SS si es necesario
          data.start_time = this.extendData.start_time.includes(':') && this.extendData.start_time.split(':').length === 2
            ? this.extendData.start_time + ':00'
            : this.extendData.start_time
        }
        if (this.extendData.end_time) {
          // Convertir formato HH:MM a HH:MM:SS si es necesario
          data.end_time = this.extendData.end_time.includes(':') && this.extendData.end_time.split(':').length === 2
            ? this.extendData.end_time + ':00'
            : this.extendData.end_time
        }
        if (this.extendData.parking_space_ids && this.extendData.parking_space_ids.length > 0) {
          data.parking_space_ids = this.extendData.parking_space_ids
        }
        if (this.extendData.notes) {
          data.notes = this.extendData.notes
        }

        const config = {
          method: 'patch',
          url: `https://empresa-service.intrare.app/Parking/invitations/reservations/${this.selectedReservation.id}/update/`,
          headers: {
            'Content-Type': 'application/json',
            'authorization': 'Token ' + store.getters.getToken
          },
          data: data
        }

        await axios(config)
        this.dialogExtend = false
        // Recargar lista de reservas
        await this.FETCH_RESERVATIONS({ role: 'owner' })
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Reserva modificada exitosamente')
      } catch (err) {
        // Manejo específico de errores del backend
        if (err.response && err.response.status === 403) {
          this.errorExtend = 'No eres propietario de esta reserva'
        } else if (err.response && err.response.status === 400) {
          this.errorExtend = (err.response.data && (err.response.data.error || err.response.data.detail)) || 'Datos inválidos en la solicitud'
        } else if (err.response && err.response.status === 401) {
          this.errorExtend = 'Sesión expirada. Por favor, vuelve a iniciar sesión'
        } else if (err.response && err.response.status === 404) {
          this.errorExtend = 'La reservación no existe'
        } else {
          this.errorExtend = err.message || 'Error al modificar reserva'
        }
        console.error('Error modificando reserva:', err)
      } finally {
        this.loadingExtend = false
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      return moment(dateStr).format('DD/MM/YYYY')
    },

    formatTime(timeStr) {
      if (!timeStr) return ''
      // Si es formato HH:MM:SS, extraer HH:MM
      if (timeStr.includes(':')) {
        return timeStr.split(':').slice(0, 2).join(':')
      }
      return timeStr
    },

    formatStatus(status) {
      const map = {
        active: 'Activa',
        expired: 'Expirada',
        canceled: 'Cancelada'
      }
      return map[status] || status
    },

    getStatusColor(status) {
      const map = {
        active: 'green',
        expired: 'orange',
        canceled: 'red'
      }
      return map[status] || 'grey'
    },

    getQRStatusLabel(status) {
      const labels = {
        pending: 'Pendiente',
        active: 'Activo',
        in_use: 'En uso',
        used: 'Usado',
        inactive: 'Inactivo',
        expired: 'Vencido'
      }
      return labels[status] || status
    },

    getQRStatusColor(status) {
      const colors = {
        pending: 'grey',
        active: 'green',
        in_use: 'blue',
        used: 'grey darken-1',
        inactive: 'orange',
        expired: 'red'
      }
      return colors[status] || 'grey'
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  opacity: 0.7;
  transition: opacity 0.2s ease-in-out;
}

.text-decoration-none {
  text-decoration: none !important;
  color: inherit;
}

.text-decoration-none:hover {
  text-decoration: underline;
}
</style>
